"use client";

import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import Image from "next/image";
import plusIcon from "@icons/plus/plus.svg";
import { AttributesType } from "@/types";
import { cn } from "@/lib/utils";

export type variants =
  | "disabled"
  | "transparent"
  | "underline"
  | "outline"
  | "close"
  | "primary"
  | "secondary"
  | "plus"
  | "icon"
  | "text"
  | "gradient"
  | "primary-light"
  | "secondary-light"
  | "pagination"
  | "contentGenerationMessageAction"
  | "modalClose"
  | "modalSecondary"
  | "modalPrimary";

export const buttonVariants = cva(
  [
    "w-full",
    "uppercase",
    "min-h-[3rem]",
    "px-4",
    "py-3",
    "rounded-lg",
    "font-paytone",
    "text-base",
    "font-normal",
    "leading-normal",
    "tracking-[-0.02em]",
    "outline-none",
    "transition-all",
  ],
  {
    variants: {
      variant: {
        primary: ["bg-primary-action-card-gradient", "text-secondary-white", "hover:opacity-90"],
        "primary-light": ["bg-purple-100", "text-purple-500"],
        secondary: ["bg-secondary-white", "text-primary", "border", "border-[2px]", "border-primary"],
        "secondary-light": ["bg-secondary-white", "text-primary", "border", "border-[2px]", "border-secondary-light"],
        disabled: ["bg-secondary-light", "text-secondary-white", "cursor-not-allowed"],
        transparent: [
          "bg-transparent",
          "!w-auto",
          "!font-epilogue",
          "!font-[700]",
          "border",
          "border-[2px]",
          "border-primary",
          "text-primary",
          "text-base",
          "px-4",
          "py-2",
          "normal-case",
        ],
        plus: [
          "!w-[40px]",
          "!h-[40px]",
          "!rounded-full",
          "bg-mondelez-light-purple",
          "flex",
          "items-center",
          "justify-center",
          "transition-colors",
          "duration-200",
          "hover:bg-mondelez-primary-purple",
          "active:bg-mondelez-primary-purple",
        ],
        underline: [
          "text-mondelez-primary-purple",
          "!font-epilogue",
          "!w-fit",
          "!normal-case",
          "text-left",
          "text-base",
          "!font-bold",
          "!bg-transparent",
          "!border-none",
          "cursor-pointer",
          "p-0",
          "underline",
        ],
        text: [
          "text-mondelez-primary-purple",
          "!font-epilogue",
          "!w-fit",
          "!normal-case",
          "text-left",
          "text-base",
          "!font-bold",
          "!bg-transparent",
          "!border-none",
          "cursor-pointer",
          "p-0",
        ],
        outline: [
          "text-mondelez-primary-purple",
          "!font-epilogue",
          "!w-fit",
          "!normal-case",
          "text-left",
          "!font-bold",
          "!bg-transparent",
          "!border-2",
          "border-primary",
          "cursor-pointer",
        ],
        close: ["!bg-transparent", "!w-fit", "mb-1.5"],
        icon: ["!w-fit", "!py-0", "!h-auto", "!px-0", "!min-h-0"],
        gradient: ["bg-custom-purple-gradient", "text-secondary-white"],
        contentGenerationMessageAction: [
          "inline-flex",
          "!min-h-0",
          "!w-fit",
          "!p-0",
          "items-center",
          "gap-2",
          "!border-0",
          "!bg-transparent",
          "!font-epilogue",
          "!text-xs",
          "!font-normal",
          "!leading-4",
          "!text-inherit",
          "normal-case",
          "no-underline",
          "shadow-none",
          "transition-colors",
          "hover:!text-mondelez-primary-purple",
        ],
        modalClose: [
          "absolute",
          "right-0",
          "top-[-1.9375rem]",
          "z-10",
          "flex",
          "!min-h-0",
          "!w-fit",
          "items-center",
          "justify-center",
          "!gap-[0.625rem]",
          "rounded-b-none",
          "rounded-tl-[1rem]",
          "rounded-tr-[1rem]",
          "bg-white",
          "pl-[0.5rem]",
          "pr-[1rem]",
          "pb-[1rem]",
          "pt-[0.5rem]",
          "!font-epilogue",
          "text-[1rem]",
          "font-bold",
          "leading-[1.4375rem]",
          "tracking-normal",
          "normal-case",
          "text-purple-500",
          "shadow-none",
          "outline-none",
          "transition-none",
        ],
        modalSecondary: [
          "!h-[2.75rem]",
          "!max-h-[2.75rem]",
          "!min-h-[2.75rem]",
          "!w-fit",
          "!gap-[0.5rem]",
          "rounded-[0.5rem]",
          "border",
          "border-secondary-light",
          "bg-white",
          "px-[0.75rem]",
          "py-0",
          "!font-epilogue",
          "text-[1rem]",
          "font-bold",
          "leading-[1.4375rem]",
          "tracking-normal",
          "normal-case",
          "text-purple-500",
          "transition-none",
        ],
        modalPrimary: [
          "!h-[2.75rem]",
          "!max-h-[2.75rem]",
          "!min-h-[2.75rem]",
          "!w-fit",
          "!gap-[0.5rem]",
          "rounded-[0.5rem]",
          "bg-primary-action-card-gradient",
          "px-[0.75rem]",
          "py-0",
          "!font-epilogue",
          "text-[1rem]",
          "font-bold",
          "leading-[1.4375rem]",
          "tracking-normal",
          "normal-case",
          "text-secondary-white",
          "transition-none",
        ],
        pagination: [
          "flex",
          "!font-bold",
          "!font-epilogue",
          "!normal-case",
          "items-center",
          "justify-center",
          "bg-white",
          "border-2",
          "border-mondelez-primary-purple",
          "text-mondelez-primary-purple",
          "transition-all",
          "duration-200",
          "focus:outline-none",
          "disabled:border-mondelez-tints-purple",
          "disabled:text-mondelez-tints-purple",
          "disabled:cursor-not-allowed",
          "disabled:opacity-60",
        ],
        secondaryBackgroundLight: [
          "h-11 min-h-11 max-h-11 rounded-lg border border-secondary-light bg-purple-100 text-mondelez-primary-purple text-base font-bold leading-[23px] font-epilouge normal-case",
        ],
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  children?: React.ReactNode;
  icon?: string;
  dataAttributes?: AttributesType;
  iconAlt?: string;
  projectHeadingID?: string;
  buttonType?: "button" | "submit" | "reset";
  ariaHiddenIcon?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, icon, children, dataAttributes = {}, iconAlt, projectHeadingID, buttonType, ariaHiddenIcon, ...props }, ref) => {
    let content;

    switch (variant) {
      case "plus":
        content = <Image src={plusIcon} alt={iconAlt ?? "plus icon"} />;
        break;

      case "icon":
        if (icon) {
          content = <Image src={icon} aria-hidden={ariaHiddenIcon} alt={iconAlt ?? "button icon"} />;
        } else {
          content = children;
        }
        break;

      case "pagination":
        content = children;
        break;

      case "primary":
      case "secondary":
      case "disabled":
      case "transparent":
      case "text":
      case "gradient":
      default:
        if (typeof children === "string") {
          content = children.length > 30 ? `${children.slice(0, 30)}...` : children;
        } else {
          content = children;
        }
        break;
    }

    return (
      <button
        type={buttonType ?? "button"}
        className={cn(`${buttonVariants({ variant })} ${className}`)}
        ref={ref}
        disabled={variant === "disabled"}
        {...(projectHeadingID ? { "aria-describedby": projectHeadingID } : {})}
        {...(Object.keys(dataAttributes).length > 0 ? dataAttributes : {})}
        {...props}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = "Button";

export default Button;
